import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { formatCurrency } from "@/lib/currencyFormatter";
import { Settings2, Plus } from "lucide-react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import "./MenuSection.css";
import { useMenuStore } from "@/store/menuStore";
import { useMenuCategories, useMenuItems } from "@/services/menu/menuItem/fetchMenuItems";
import { MenuItem, CategoryWithMenuItems } from "@/types/menu";
import { useTranslation } from "react-i18next";

interface MenuSectionProps {
  storeId: string;
  currency: string;
  colors: {
    primary: string;
    secondary: string;
    accent: string;
    background: string;
    text: string;
  };
}

export default function MenuSection({
  storeId,
  currency,
  colors,
}: MenuSectionProps) {
  const { t } = useTranslation();
  const [activeCategory, setActiveCategory] = useState<string>("all");
  const { data: categories = [], isLoading, error } = useMenuCategories(storeId);
  const { data: menuItems = [] } = useMenuItems(storeId);

  // Create categoriesWithItems structure for backward compatibility
  const categoriesWithItems: CategoryWithMenuItems[] = categories.map(category => ({
    ...category,
    menu_items: menuItems.filter(item => 
      item.categoryIds?.includes(category.id)
    )
  }));

  // No need to fetch manually - TanStack Query handles this

  useEffect(() => {
    if (categories.length > 0 && !activeCategory) {
      setActiveCategory("all");
    }
  }, [categories, activeCategory]);

  console.log({ categoriesWithItems });

  if (isLoading) {
    return (
      <section
        className="py-16 px-4"
        style={{ backgroundColor: colors.background }}
      >
        <div className="container mx-auto max-w-7xl">
          <div className="space-y-8 animate-pulse">
            <div className="text-center">
              <div className="h-10 bg-gray-200 rounded-md w-48 mx-auto mb-4"></div>
              <div className="h-4 bg-gray-200 rounded-md w-80 mx-auto"></div>
            </div>
            <div className="flex justify-center">
              <div className="h-10 bg-gray-200 rounded-md w-80"></div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <div
                  key={i}
                  className="bg-white rounded-xl shadow-sm overflow-hidden"
                >
                  <div className="h-48 bg-gray-200"></div>
                  <div className="p-6 space-y-3">
                    <div className="h-6 bg-gray-200 rounded w-3/4"></div>
                    <div className="h-4 bg-gray-200 rounded w-1/2"></div>
                    <div className="h-4 bg-gray-200 rounded w-full"></div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section
        className="py-16 px-4"
        style={{ backgroundColor: colors.background }}
      >
        <div className="container mx-auto max-w-7xl">
          <div className="text-center py-16">
            <p className="text-lg" style={{ color: colors.text }}>
              {t("storefront.menu.loading.error")}
            </p>
          </div>
        </div>
      </section>
    );
  }

  if (categoriesWithItems.length === 0) {
    return (
      <section
        className="py-16 px-4"
        style={{ backgroundColor: colors.background }}
      >
        <div className="container mx-auto max-w-7xl">
          <div className="text-center py-16">
            <h2
              className="text-3xl font-bold mb-4"
              style={{ color: colors.text }}
            >
              {t("storefront.menu.title")}
            </h2>
            <p className="text-lg opacity-70" style={{ color: colors.text }}>
              {t("storefront.menu.empty.message")}
            </p>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section
      className="py-16 px-4"
      style={{ backgroundColor: colors.background }}
    >
      <div className="container mx-auto max-w-7xl">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <h2
            className="text-3xl md:text-4xl font-bold mb-4"
            style={{ color: colors.text }}
          >
            {t("storefront.menu.title")}
          </h2>
          <p
            className="text-base md:text-lg opacity-80 max-w-2xl mx-auto"
            style={{ color: colors.text }}
          >
            {t("storefront.menu.subtitle")}
          </p>
        </motion.div>

        {/* Category Filter Buttons */}
        <div className="flex justify-center mb-12">
          <div className="flex flex-wrap justify-center gap-2 max-w-4xl">
            <button
              onClick={() => setActiveCategory("all")}
              className={`px-4 py-2 text-sm font-medium rounded-full border transition-all duration-300 ${
                activeCategory === "all"
                  ? "text-white shadow-md"
                  : "text-gray-600 bg-white hover:shadow-sm"
              }`}
              style={{
                backgroundColor:
                  activeCategory === "all" ? colors.primary : "white",
                borderColor:
                  activeCategory === "all" ? colors.primary : "#e5e7eb",
              }}
            >
              {t("storefront.menu.allItems")}
            </button>
            {categories.map((category) => (
              <button
                key={category.id}
                onClick={() => setActiveCategory(category.id)}
                className={`px-4 py-2 text-sm font-medium rounded-full border transition-all duration-300 ${
                  activeCategory === category.id
                    ? "text-white shadow-md"
                    : "text-gray-600 bg-white hover:shadow-sm"
                }`}
                style={{
                  backgroundColor:
                    activeCategory === category.id ? colors.primary : "white",
                  borderColor:
                    activeCategory === category.id ? colors.primary : "#e5e7eb",
                }}
              >
                {category.name || t("storefront.menu.category")}
              </button>
            ))}
          </div>
        </div>

        {/* Menu Items Display */}
        {activeCategory === "all" ? (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
          >
            {categoriesWithItems.flatMap((category) =>
              category.menu_items.map((item, index) => (
                <MenuItemCard
                  key={`${category.id}-${item.id}`}
                  item={item}
                  currency={currency}
                  colors={colors}
                  delay={index * 0.05}
                />
              ))
            )}
          </motion.div>
        ) : (
          categoriesWithItems.map((category) => (
            <div
              key={category.id}
              className={`transition-all duration-300 ${
                activeCategory === category.id ? "block" : "hidden"
              }`}
            >
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3 }}
                className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
              >
                {category.menu_items.map((item, index) => (
                  <MenuItemCard
                    key={item.id}
                    item={item}
                    currency={currency}
                    colors={colors}
                    delay={index * 0.05}
                  />
                ))}
              </motion.div>
            </div>
          ))
        )}
      </div>
    </section>
  );
}

interface MenuItemCardProps {
  item: MenuItem;
  currency: string;
  colors: {
    primary: string;
    secondary: string;
    accent: string;
    background: string;
    text: string;
  };
  delay?: number;
}

function MenuItemCard({
  item,
  currency,
  colors,
  delay = 0,
}: MenuItemCardProps) {
  const { t } = useTranslation();

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay }}
      className="group bg-white rounded-2xl shadow-sm hover:shadow-lg transition-all duration-300 overflow-hidden border border-gray-100"
    >
      <div className="relative h-48 overflow-hidden">
        {item.image_url ? (
          <>
            <img
              src={item.image_url}
              alt={item.name}
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/20 via-transparent to-transparent"></div>
          </>
        ) : (
          <div
            className="w-full h-full flex items-center justify-center"
            style={{ backgroundColor: `${colors.primary}08` }}
          >
            <div className="text-center">
              <div
                className="w-12 h-12 mx-auto mb-2 rounded-full flex items-center justify-center"
                style={{ backgroundColor: `${colors.primary}20` }}
              >
                <svg
                  className="w-6 h-6"
                  style={{ color: colors.primary }}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                  />
                </svg>
              </div>
              <p className="text-xs text-gray-400">
                {t("storefront.menu.noImage")}
              </p>
            </div>
          </div>
        )}
      </div>

      <div className="p-5">
        <div className="flex justify-between items-start mb-3">
          <h3 className="text-lg font-semibold text-gray-900 line-clamp-2 flex-1 pr-3">
            {item.name}
          </h3>
          <div
            className="text-lg font-bold px-3 py-1 rounded-full flex-shrink-0"
            style={{
              backgroundColor: `${colors.primary}12`,
              color: colors.primary,
            }}
          >
            {currency}
            {typeof item.price === "number"
              ? item.price.toFixed(2)
              : item.price}
          </div>
        </div>

        {item.description && (
          <p className="text-sm text-gray-600 leading-relaxed line-clamp-2 mb-4">
            {item.description}
          </p>
        )}

        {item.dietary_info && item.dietary_info.length > 0 && (
          <div className="flex flex-wrap gap-1">
            {item.dietary_info.slice(0, 3).map((info, index) => (
              <span
                key={index}
                className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium"
                style={{
                  backgroundColor: `${colors.accent}15`,
                  color: colors.accent,
                }}
              >
                {info}
              </span>
            ))}
            {item.dietary_info.length > 3 && (
              <span
                className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium"
                style={{
                  backgroundColor: `${colors.accent}15`,
                  color: colors.accent,
                }}
              >
                +{item.dietary_info.length - 3}
              </span>
            )}
          </div>
        )}
      </div>
    </motion.div>
  );
}
